import React, { useEffect, useMemo, useState } from "react";
import { FaArrowLeft } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import CustomerSearchSelect from "../components/CustomerSearchSelect";
import "../assets/css/AddLead.css";
import "../assets/css/Issues.css";
import "../assets/css/Permissions.css";
import { getAuthItem } from "../utils/authStorage";
import { formatPascal } from "../utils/formatters";
import { formValueWithMobileSanitize, mobileValidationMessage } from "../utils/phone";
import { BUSINESS_MODEL_OPTIONS, leadFieldProfile, projectOptionLabel } from "../utils/leadProfile";

function safeString(v) {
  return v == null ? "" : String(v);
}

function getCreatedBy() {
  try {
    const userStr = getAuthItem("user");
    if (!userStr) return 1;
    const user = JSON.parse(userStr);
    return user.user_id || user.id || 1;
  } catch {
    return 1;
  }
}

function getId(v) {
  if (v == null || v === "") return null;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? null : n;
}

function parseDecimal(v) {
  if (v == null || v === "") return null;
  const s = String(v).trim().replace(/,/g, ".");
  const n = parseFloat(s);
  return Number.isNaN(n) ? null : n;
}

const REQUIREMENT_TYPES = ["Rent", "Buy", "Lease", "Accommodation"];

const EMPTY_FORM = {
  clientId: "",
  tenantClientId: "",
  projectId: "",
  serviceId: "",
  propertyService: "",
  propertyServiceLink: "",
  tenantProjectName: "",
  firstName: "",
  lastName: "",
  email: "",
  mobileNumber: "",
  altPhone: "",
  requirementType: "",
  propertyTypeId: "",
  budgetMin: "",
  budgetMax: "",
  preferredLocation: "",
  propertyLocation: "",
  sharingPreference: "",
  moveInDate: "",
  leadStatusId: "",
  sourceId: "",
  dmCampaignId: "",
  dmAdId: "",
  domainId: "",
  businessModel: "",
  assignedTo: "",
  siteVisit: false,
  isOpportunity: false,
  automationEnabled: false,
  notes: "",
};

function AddRealestateLead({ onBack, onAddLead }) {
  const createdBy = getCreatedBy();

  const [formData, setFormData] = useState(() => ({
    ...EMPTY_FORM,
    clientId: sessionStorage.getItem("digixRealestateTenantId") || "",
  }));
  const [mobileCountryCode, setMobileCountryCode] = useState("+91");
  const handleClear = () => {
    if (!window.confirm("Clear all entered details?")) return;
    setFormData((p) => ({ ...EMPTY_FORM, clientId: p.clientId, assignedTo: p.assignedTo }));
    setMobileCountryCode("+91");
  };
  const [countries, setCountries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [tenantClients, setTenantClients] = useState([]);
  const [clientProjects, setClientProjects] = useState([]);
  const [clientServices, setClientServices] = useState([]);
  const [loadingTenantClients, setLoadingTenantClients] = useState(false);
  const [loadingClientAssets, setLoadingClientAssets] = useState(false);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [businessTypes, setBusinessTypes] = useState([]);

  const selectedCustomer = customers.find((c) => String(c.id) === String(formData.clientId));
  const capturesAllBusinessLeads = !!selectedCustomer?.capturesAllBusinessLeads;
  // Other clients default to their own model; staff can still switch it per lead.
  const leadModel = capturesAllBusinessLeads
    ? formData.businessModel
    : formData.businessModel || selectedCustomer?.businessModel || "";
  const profile = leadFieldProfile(
    capturesAllBusinessLeads
      ? businessTypes.find((d) => String(d.id) === String(formData.domainId))?.name
      : selectedCustomer?.businessType,
    leadModel
  );
  const offeringWord = profile.project ? "projects" : profile.service ? "services" : "projects or services";
  const ownerRequired = !!formData.clientId && !loadingTenantClients && tenantClients.length > 0;

  const useProjectMode = !loadingClientAssets && profile.project && clientProjects.length > 0;
  const useServiceMode = !loadingClientAssets && profile.service && clientServices.length > 0;

  const phoneCountryOptions = useMemo(() => {
    return (countries || []).map((c) => ({
      id: c.country_id ?? c.Country_Id ?? c.id,
      code: (c.country_code || c.country_short_code || "").trim(),
      name: c.country_name || c.Country_Name || c.name || "",
    })).filter((x) => x.code);
  }, [countries]);


  // Fetch active DigiX staff users for Assigned To
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const response = await axiosInstance.get("/users", { params: { status: "Active" } });
        const list = Array.isArray(response.data) ? response.data : [];
        const mapped = list
          .map((u) => ({
            id: u.user_id ?? u.User_Id ?? u.id,
            name: [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || u.email || `User #${u.user_id}`,
          }))
          .filter((u) => u.id != null)
          .sort((a, b) => a.name.localeCompare(b.name));
        setUsers(mapped);
        let currentId = "";
        try {
          const userStr = getAuthItem("user");
          if (userStr) {
            const user = JSON.parse(userStr);
            currentId = String(user.user_id ?? user.id ?? "");
          }
        } catch {
          /* ignore */
        }
        setFormData((prev) => {
          if (prev.assignedTo) return prev;
          const fallback = currentId || (mapped[0] ? String(mapped[0].id) : "");
          return { ...prev, assignedTo: fallback };
        });
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [propertyRes, sourceRes, statusRes, countriesRes, customersRes, domainsRes] = await Promise.all([
          axiosInstance.get("/property-types").catch(() => ({ data: [] })),
          axiosInstance.get("/lead-sources"),
          axiosInstance.get("/lead-statuses"),
          axiosInstance.get("/countries").catch(() => ({ data: [] })),
          axiosInstance.get("/clients").catch(() => ({ data: [] })),
          axiosInstance.get("/domains").catch(() => ({ data: [] })),
        ]);

        const rawDomains = Array.isArray(domainsRes.data) ? domainsRes.data : domainsRes.data?.data || [];
        setBusinessTypes(
          rawDomains
            .map((d) => ({ id: d.Domain_Id, name: d.Domain_Name, defaultModel: d.Default_Business_Model || "" }))
            .filter((d) => d.id != null && d.name)
        );

        const rawCountries = Array.isArray(countriesRes?.data)
          ? countriesRes.data
          : Array.isArray(countriesRes?.data?.data)
            ? countriesRes.data.data
            : [];
        setCountries(rawCountries);

        const rawCustomers = Array.isArray(customersRes.data)
          ? customersRes.data
          : Array.isArray(customersRes.data?.results)
            ? customersRes.data.results
            : Array.isArray(customersRes.data?.data)
              ? customersRes.data.data
              : [];
        setCustomers(
          rawCustomers
            .map((item) => {
              const c = item.client || item;
              const id = c.client_id ?? c.client_id ?? c.id;
              const first = c.First_Name || c.first_name || "";
              const last = c.Last_Name || c.last_name || "";
              return {
                id,
                name: `${first} ${last}`.trim() || `Customer #${id}`,
                firstName: first,
                lastName: last,
                email: c.Email || c.email || "",
                phone: c.Phone || c.phone || "",
                countryCode: c.Country_Code || c.country_code || "+91",
                altPhone: c.Alt_Phone || c.alt_phone || "",
                capturesAllBusinessLeads: !!c.Captures_All_Business_Leads,
                businessModel: c.Business_Model || "",
                businessType: c.Domain_Name || "",
              };
            })
            .filter((c) => c.id != null)
        );

        const rawPropertyTypes = Array.isArray(propertyRes.data)
          ? propertyRes.data
          : Array.isArray(propertyRes.data?.data)
            ? propertyRes.data.data
            : [];
        setPropertyTypes(
          rawPropertyTypes
            .map((p) => ({
              id: p.Property_Type_Id ?? p.property_type_id ?? p.id,
              name: p.Property_Type_Name ?? p.property_type_name ?? p.name ?? "",
            }))
            .filter((p) => p.id != null && String(p.name).trim() !== "")
        );

        const rawSources = Array.isArray(sourceRes.data)
          ? sourceRes.data
          : Array.isArray(sourceRes.data?.data)
            ? sourceRes.data.data
            : [];
        setSources(
          rawSources
            .map((s) => ({
              id: s.Source_Id ?? s.source_id ?? s.id,
              name: s.Source_Name ?? s.source_name ?? s.name ?? "",
            }))
            .filter((s) => s.id != null && String(s.name).trim() !== "")
        );

        const rawStatuses = Array.isArray(statusRes.data)
          ? statusRes.data
          : Array.isArray(statusRes.data?.data)
            ? statusRes.data.data
            : [];
        const mappedStatuses = rawStatuses
          .map((s) => ({
            id: s.Status_Id ?? s.status_id ?? s.id,
            name: s.Status ?? s.status ?? s.name ?? "",
          }))
          .filter((s) => s.id != null && String(s.name).trim() !== "");
        setStatuses(mappedStatuses);
        const newStatus = mappedStatuses.find(
          (s) => String(s.name).trim().toLowerCase() === "new"
        );
        if (newStatus) {
          setFormData((prev) => ({
            ...prev,
            leadStatusId: prev.leadStatusId || String(newStatus.id),
          }));
        }
      } catch (err) {
        console.error("Error fetching real estate lead lookups:", err);
      }
    };
    fetchLookups();
  }, []);

  useEffect(() => {
    let active = true;
    const tenantId = getId(formData.clientId);
    if (!tenantId) {
      setTenantClients([]);
      return undefined;
    }
    setLoadingTenantClients(true);
    (async () => {
      try {
        const res = await axiosInstance.get("/tenant-clients/", {
          params: { tenant_id: tenantId },
        });
        const raw = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
        if (!active) return;
        setTenantClients(
          raw
            .map((row) => ({
              id: row.Tenant_Client_Id ?? row.tenant_client_id ?? row.id,
              name: row.Client_Name || row.client_name || `Client #${row.Tenant_Client_Id}`,
              status: row.Status || row.status || "Active",
            }))
            .filter((c) => c.id != null && String(c.status).toLowerCase() !== "inactive")
        );
      } catch (err) {
        console.error("Error fetching tenant clients:", err);
        if (active) setTenantClients([]);
      } finally {
        if (active) setLoadingTenantClients(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [formData.clientId]);

  useEffect(() => {
    let active = true;
    const tenantId = getId(formData.clientId);
    setCampaigns([]);
    if (!tenantId) return undefined;
    const params = { params: { tenant_id: tenantId } };
    axiosInstance
      .get("/dm/campaign-options", params)
      .catch(() => axiosInstance.get("/client/dm/campaign-options", params))
      .then((res) => active && setCampaigns(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => active && setCampaigns([]));
    return () => {
      active = false;
    };
  }, [formData.clientId]);

  const selectedCampaign = campaigns.find((c) => String(c.Campaign_Id) === String(formData.dmCampaignId));

  useEffect(() => {
    let active = true;
    const tenantClientId = getId(formData.tenantClientId);
    if (!tenantClientId) {
      setClientProjects([]);
      setClientServices([]);
      return undefined;
    }
    setLoadingClientAssets(true);
    (async () => {
      try {
        const [projectsRes, servicesRes] = await Promise.all([
          axiosInstance.get("/tenant-projects/", {
            params: { tenant_client_id: tenantClientId },
          }),
          axiosInstance.get("/tenant-client-services/", {
            params: { tenant_client_id: tenantClientId },
          }),
        ]);
        const projectsRaw = Array.isArray(projectsRes.data?.data)
          ? projectsRes.data.data
          : Array.isArray(projectsRes.data)
            ? projectsRes.data
            : [];
        const servicesRaw = Array.isArray(servicesRes.data?.data)
          ? servicesRes.data.data
          : Array.isArray(servicesRes.data)
            ? servicesRes.data
            : [];
        if (!active) return;
        setClientProjects(
          projectsRaw
            .map((row) => ({
              id: row.Tenant_Project_Id ?? row.tenant_project_id ?? row.id,
              name: row.Project_Name || row.project_name || `Project #${row.Tenant_Project_Id}`,
              label: projectOptionLabel(
                row.Project_Name || row.project_name || `Project #${row.Tenant_Project_Id}`,
                row.Address
              ),
              status: row.Status || row.status || "Active",
            }))
            .filter((p) => p.id != null && String(p.status).toLowerCase() !== "inactive")
        );
        setClientServices(
          servicesRaw
            .map((row) => ({
              id: row.Tenant_Client_Service_Id ?? row.tenant_client_service_id ?? row.id,
              name: row.Service_Name || row.service_name || `Service #${row.Tenant_Client_Service_Id}`,
              status: row.Status || row.status || "Active",
            }))
            .filter((s) => s.id != null && String(s.status).toLowerCase() !== "inactive")
        );
      } catch (err) {
        console.error("Error fetching client projects/services:", err);
        if (active) {
          setClientProjects([]);
          setClientServices([]);
        }
      } finally {
        if (active) setLoadingClientAssets(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [formData.tenantClientId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox" ? checked : formValueWithMobileSanitize(name, value);
    setFormData((p) => ({ ...p, [name]: nextValue }));
  };

  const handleSelectClient = (clientId) => {
    const id = String(clientId || "");
    setFormData((p) => ({
      ...p,
      clientId: id,
      tenantClientId: "",
      projectId: "",
      serviceId: "",
      propertyService: "",
      tenantProjectName: "",
      dmCampaignId: "",
      dmAdId: "",
      domainId: "",
      businessModel: "",
    }));
    setTenantClients([]);
    setClientProjects([]);
    setClientServices([]);
  };

  const resetOffering = {
    tenantClientId: "",
    projectId: "",
    serviceId: "",
    propertyService: "",
    tenantProjectName: "",
    sharingPreference: "",
    moveInDate: "",
    propertyTypeId: "",
    propertyLocation: "",
    requirementType: "",
  };

  const handleSelectBusinessType = (e) => {
    const id = e.target.value;
    const type = businessTypes.find((d) => String(d.id) === String(id));
    setFormData((p) => ({ ...p, ...resetOffering, domainId: id, businessModel: type?.defaultModel || "" }));
    setClientProjects([]);
    setClientServices([]);
  };

  const handleSelectBusinessModel = (e) => {
    const value = e.target.value;
    setFormData((p) => ({ ...p, ...resetOffering, tenantClientId: p.tenantClientId, businessModel: value }));
  };

  const handleSelectTenantClient = (e) => {
    const id = e.target.value;
    setFormData((p) => ({
      ...p,
      tenantClientId: id,
      projectId: "",
      serviceId: "",
      propertyService: "",
      tenantProjectName: "",
    }));
    setClientProjects([]);
    setClientServices([]);
  };

  const handleSelectProject = (e) => {
    const id = e.target.value;
    const project = clientProjects.find((p) => String(p.id) === String(id));
    const name = project?.name || "";
    setFormData((p) => ({
      ...p,
      projectId: id,
      serviceId: "",
      tenantProjectName: name,
      propertyService: name,
      ...(id
        ? {}
        : { sharingPreference: "", moveInDate: "" }),
    }));
  };

  const handleSelectService = (e) => {
    const id = e.target.value;
    const service = clientServices.find((s) => String(s.id) === String(id));
    const name = service?.name || "";
    setFormData((p) => ({
      ...p,
      serviceId: id,
      projectId: "",
      tenantProjectName: "",
      propertyService: name,
      sharingPreference: "",
      moveInDate: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredFields = [
      ["clientId", formData.clientId],
      ["firstName", formData.firstName],
      ["mobileNumber", formData.mobileNumber],
      ["sourceId", formData.sourceId],
      ["leadStatusId", formData.leadStatusId],
    ];

    if (ownerRequired) requiredFields.push(["tenantClientId", formData.tenantClientId]);
    if (useProjectMode) {
      requiredFields.push(["projectId", formData.projectId]);
    } else if (useServiceMode) {
      requiredFields.push(["serviceId", formData.serviceId]);
    }

    if (capturesAllBusinessLeads && !formData.domainId) {
      alert("Please select the lead's business type.");
      return;
    }
    if (formData.clientId && !profile.model) {
      alert("Please select the business model (Project Based or Service Based).");
      return;
    }

    const missing = requiredFields
      .filter(([, v]) => !safeString(v).trim())
      .map(([k]) => k);
    if (missing.length) {
      alert(
        `Please select client, ${profile.ownerLabel.toLowerCase()}, ${profile.offeringLabel.toLowerCase()}, and fill all required lead fields.`
      );
      return;
    }
    const mobileError = mobileValidationMessage(formData.mobileNumber, { required: true, label: "Mobile number" });
    if (mobileError) {
      alert(mobileError);
      return;
    }
    const altError = mobileValidationMessage(formData.altPhone, { label: "Alternate phone" });
    if (altError) {
      alert(altError);
      return;
    }

    const budgetMin = parseDecimal(formData.budgetMin);
    const budgetMax = parseDecimal(formData.budgetMax);
    if (formData.budgetMin && (budgetMin == null || budgetMin < 0)) {
      alert("Please enter a valid number for Budget Min.");
      return;
    }
    if (formData.budgetMax && (budgetMax == null || budgetMax < 0)) {
      alert("Please enter a valid number for Budget Max.");
      return;
    }
    if (budgetMin != null && budgetMax != null && budgetMin > budgetMax) {
      alert("Budget Min cannot be greater than Budget Max.");
      return;
    }

    const createdByNumber = getId(createdBy) ?? 1;

    const payload = {
      client_id: getId(formData.clientId),
      Property_Service: formData.propertyService.trim() || null,
      Property_Service_Link: profile.showLink ? formData.propertyServiceLink.trim() || null : null,
      Tenant_Project_Name: formData.tenantProjectName.trim() || null,
      Tenant_Project_Id: useProjectMode && formData.projectId ? Number(formData.projectId) : null,
      Domain_Id: capturesAllBusinessLeads ? getId(formData.domainId) : null,
      Business_Model: profile.model || null,
      First_Name: formatPascal(formData.firstName.trim()),
      Last_Name: formData.lastName.trim() ? formatPascal(formData.lastName.trim()) : null,
      Email: formData.email.trim() || null,
      Country_Code: mobileCountryCode.trim() || "+91",
      Mobile_Number: formData.mobileNumber.trim(),
      Alt_Phone: formData.altPhone.trim() || null,
      Requirement_Type: profile.showRequirementType ? formData.requirementType || null : null,
      Property_Type_Id: profile.showPropertyDetails ? getId(formData.propertyTypeId) : null,
      Budget_Min: profile.showBudget && budgetMin != null ? budgetMin : null,
      Budget_Max: profile.showBudget && budgetMax != null ? budgetMax : null,
      Preferred_Location: profile.showPreferredLocation ? formData.preferredLocation.trim() || null : null,
      Property_Location: profile.showPropertyDetails ? formData.propertyLocation.trim() || null : null,
      Sharing_Preference: profile.showPropertyDetails ? formData.sharingPreference.trim() || null : null,
      Move_In_Date:
        profile.showPropertyDetails || profile.showPreferredDate ? formData.moveInDate || null : null,
      Lead_Status_Id: getId(formData.leadStatusId),
      Source_Id: getId(formData.sourceId),
      Dm_Campaign_Id: getId(formData.dmCampaignId),
      Dm_Ad_Id: formData.dmCampaignId ? getId(formData.dmAdId) : null,
      Site_Visit: profile.showSiteVisit && !!formData.siteVisit,
      Is_Opportunity: !!formData.isOpportunity,
      Automation_Enabled: !!formData.automationEnabled,
      Notes: formData.notes.trim() || null,
      Created_By: createdByNumber,
      Assigned_To: getId(formData.assignedTo) ?? createdByNumber,
    };

    setSubmitting(true);
    try {
      const response = await axiosInstance.post("/create-real-estate-lead", payload);
      const msg =
        response.data?.message ||
        response.data?.error ||
        "Real estate lead created successfully.";

      alert(msg);

      if (response.data && onAddLead) {
        onAddLead(response.data);
      }
      if (onBack) onBack();
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to create real estate lead.";
      console.error("Error creating real estate lead:", err);
      alert(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="addlead-container">
      <button type="button" className="back-link" onClick={onBack}>
        <FaArrowLeft /> Back
      </button>
      <div className="issues-main-card">
      <form className="addlead-form re-grouped-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <h3 className="re-form-section-title">Lead Contact</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>
                First Name <span className="required">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>
                Mobile Number <span className="required">*</span>
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <select
                  value={mobileCountryCode}
                  onChange={(e) => setMobileCountryCode(e.target.value)}
                  style={{ maxWidth: 120 }}
                >
                  <option value="+91">+91</option>
                  {phoneCountryOptions.map((c) => (
                    <option key={c.id ?? c.code} value={c.code}>
                      {c.code}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  inputMode="numeric"
                  maxLength={10}
                  pattern="[0-9]{10}"
                  title="Enter exactly 10 digits"
                  required
                  style={{ flex: 1 }}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Alt Phone</label>
              <input
                type="tel"
                name="altPhone"
                value={formData.altPhone}
                onChange={handleChange}
                inputMode="numeric"
                maxLength={10}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3 className="re-form-section-title">Client</h3>
          <div className="form-grid">
            <div className="form-group re-field-tenant">
              <label>
                Client <span className="required">*</span>
              </label>
              <CustomerSearchSelect
                customers={customers}
                value={formData.clientId}
                onChange={handleSelectClient}
              />
            </div>

            {capturesAllBusinessLeads ? (
              <div className="form-group">
                <label>
                  Lead Business Type <span className="required">*</span>
                </label>
                <select name="domainId" value={formData.domainId} onChange={handleSelectBusinessType} required>
                  <option value="">Select business type</option>
                  {businessTypes.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : selectedCustomer?.businessType ? (
              <div className="form-group">
                <label>Business Type</label>
                <input type="text" value={selectedCustomer.businessType} readOnly disabled />
              </div>
            ) : null}
          </div>

          <div className="form-grid re-form-row">
            <div className="form-group">
              <label>
                Business Model <span className="required">*</span>
              </label>
              <select
                name="businessModel"
                value={leadModel}
                onChange={handleSelectBusinessModel}
                disabled={!formData.clientId || (capturesAllBusinessLeads && !formData.domainId)}
                required
              >
                <option value="">
                  {!formData.clientId
                    ? "Select client first"
                    : capturesAllBusinessLeads && !formData.domainId
                      ? "Select business type first"
                      : "Select model"}
                </option>
                {BUSINESS_MODEL_OPTIONS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>
                {profile.ownerLabel} {ownerRequired ? <span className="required">*</span> : null}
              </label>
              <select
                name="tenantClientId"
                value={formData.tenantClientId}
                onChange={handleSelectTenantClient}
                disabled={!formData.clientId || loadingTenantClients || !tenantClients.length}
                required={ownerRequired}
              >
                <option value="">
                  {!formData.clientId
                    ? "Select client first"
                    : loadingTenantClients
                      ? "Loading..."
                      : tenantClients.length
                        ? `Select ${profile.ownerLabel.toLowerCase()}`
                        : `No ${profile.ownerLabel.toLowerCase()} for this client`}
                </option>
                {tenantClients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {!profile.model ? (
              <div className="form-group re-project-service-slot">
                <label>{profile.offeringLabel}</label>
                <select disabled value="">
                  <option value="">Select business model first</option>
                </select>
              </div>
            ) : formData.tenantClientId ? (
              loadingClientAssets ? (
                <div className="form-group re-project-service-slot">
                  <label>{profile.offeringLabel}</label>
                  <select disabled value="">
                    <option value="">Loading…</option>
                  </select>
                </div>
              ) : useProjectMode ? (
                <div className="form-group re-project-service-slot">
                  <label>
                    Project <span className="required">*</span>
                  </label>
                  <select
                    name="projectId"
                    value={formData.projectId}
                    onChange={handleSelectProject}
                    required
                  >
                    <option value="">Select project</option>
                    {clientProjects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : useServiceMode ? (
                <div className="form-group re-project-service-slot">
                  <label>
                    Service <span className="required">*</span>
                  </label>
                  <select
                    name="serviceId"
                    value={formData.serviceId}
                    onChange={handleSelectService}
                    required
                  >
                    <option value="">Select service</option>
                    {clientServices.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="form-group re-project-service-slot">
                  <label>{profile.offeringLabel}</label>
                  <select disabled value="">
                    <option value="">No {offeringWord} added yet</option>
                  </select>
                </div>
              )
            ) : (
              <div className="form-group re-project-service-slot">
                <label>{profile.offeringLabel}</label>
                <select disabled value="">
                  <option value="">
                    {tenantClients.length ? `Select ${profile.ownerLabel.toLowerCase()} first` : `No ${offeringWord} to pick`}
                  </option>
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="form-section" hidden={!profile.showDetails || !profile.model}>
          <h3 className="re-form-section-title">{profile.detailsTitle}</h3>
          <div className="form-grid">
            {profile.showPropertyDetails ? (
              <div className="form-group">
                <label>Property Location</label>
                <input
                  type="text"
                  name="propertyLocation"
                  value={formData.propertyLocation}
                  onChange={handleChange}
                  placeholder="Enter property location"
                />
              </div>
            ) : null}

            <div className="form-group">
              <label>{profile.linkLabel}</label>
              <input
                type="url"
                name="propertyServiceLink"
                value={formData.propertyServiceLink}
                onChange={handleChange}
                placeholder="https://example.com/..."
                maxLength={2048}
              />
            </div>

            {profile.showPropertyDetails ? (
              <div className="form-group">
                <label>Property Type</label>
                <select
                  name="propertyTypeId"
                  value={formData.propertyTypeId}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  {propertyTypes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {profile.showRequirementType ? (
              <div className="form-group">
                <label>Requirement Type</label>
                <select
                  name="requirementType"
                  value={formData.requirementType}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  {REQUIREMENT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {profile.showBudget ? (
              <>
                <div className="form-group">
                  <label>Budget Min</label>
                  <input
                    type="text"
                    name="budgetMin"
                    value={formData.budgetMin}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Budget Max</label>
                  <input
                    type="text"
                    name="budgetMax"
                    value={formData.budgetMax}
                    onChange={handleChange}
                  />
                </div>
              </>
            ) : null}

            {profile.showPreferredLocation ? (
              <div className="form-group">
                <label>Preferred Location</label>
                <input
                  type="text"
                  name="preferredLocation"
                  value={formData.preferredLocation}
                  onChange={handleChange}
                />
              </div>
            ) : null}

            {profile.showPropertyDetails ? (
              <div className="form-group">
                <label>Sharing Preference</label>
                <input
                  type="text"
                  name="sharingPreference"
                  value={formData.sharingPreference}
                  onChange={handleChange}
                  placeholder="e.g. 2 sharing, 2 BHK"
                  maxLength={100}
                />
              </div>
            ) : null}

            {profile.showPropertyDetails || profile.showPreferredDate ? (
              <div className="form-group">
                <label>{profile.showPropertyDetails ? "Move-in Date" : "Preferred Date"}</label>
                <input
                  type="date"
                  name="moveInDate"
                  value={formData.moveInDate}
                  onChange={handleChange}
                />
              </div>
            ) : null}
          </div>
        </div>

        <div className="form-section">
          <h3 className="re-form-section-title">Lead Pipeline</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>
                Source <span className="required">*</span>
              </label>
              <select
                name="sourceId"
                value={formData.sourceId}
                onChange={handleChange}
                required
              >
                <option value="">Select</option>
                {sources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            {campaigns.length ? (
              <>
                <div className="form-group">
                  <label>Campaign</label>
                  <select
                    name="dmCampaignId"
                    value={formData.dmCampaignId}
                    onChange={(e) => setFormData((p) => ({ ...p, dmCampaignId: e.target.value, dmAdId: "" }))}
                  >
                    <option value="">Not from a campaign</option>
                    {campaigns.map((c) => (
                      <option key={c.Campaign_Id} value={c.Campaign_Id}>
                        {c.Campaign_Name} · {c.Platform}
                        {c.Project_Name ? ` (${c.Project_Name})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedCampaign?.Ads?.length ? (
                  <div className="form-group">
                    <label>Ad</label>
                    <select name="dmAdId" value={formData.dmAdId} onChange={handleChange}>
                      <option value="">Any / unknown ad</option>
                      {selectedCampaign.Ads.map((a) => (
                        <option key={a.Ad_Id} value={a.Ad_Id}>
                          {a.Ad_Name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
              </>
            ) : null}
            <div className="form-group">
              <label>
                Status <span className="required">*</span>
              </label>
              <select
                name="leadStatusId"
                value={formData.leadStatusId}
                onChange={handleChange}
                required
              >
                <option value="">Select</option>
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Assigned To</label>
              <select
                name="assignedTo"
                value={formData.assignedTo}
                onChange={handleChange}
                disabled={loadingUsers}
              >
                {loadingUsers && <option value="">Loading users...</option>}
                {!loadingUsers && <option value="">Select user</option>}
                {users.map((u) => (
                  <option key={u.id} value={String(u.id)}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="re-pipeline-flags">
            {profile.showSiteVisit ? (
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="siteVisit"
                  checked={formData.siteVisit}
                  onChange={handleChange}
                />
                <span>Site Visit</span>
              </label>
            ) : null}
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isOpportunity"
                checked={formData.isOpportunity}
                onChange={handleChange}
              />
              <span>Opportunity</span>
            </label>
            <label className="automation-switch">
              <input
                type="checkbox"
                name="automationEnabled"
                checked={formData.automationEnabled}
                onChange={handleChange}
              />
              <span className="automation-switch-track" aria-hidden="true">
                <span className="automation-switch-thumb" />
              </span>
              <span>Automation</span>
            </label>
          </div>
        </div>

        <div className="form-section">
          <h3 className="re-form-section-title">Notes</h3>
          <div className="form-grid">
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                aria-label="Notes"
              />
            </div>
          </div>
        </div>

        <div className="form-actions re-form-actions">
          <button type="button" className="btn-clear-lead" onClick={handleClear} disabled={submitting}>
            Clear
          </button>
          <button type="submit" className="btn-save-lead" disabled={submitting}>
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}

export default AddRealestateLead;
